import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { AlertsType } from '@/constants/alerts.constant';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import CopyVariable from '../../../../common/CopyVariable';
import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import VideoScenarioDeleteButton from './video-scenario-delete-button';
import ScenarioSide from './video-scenario-side';

export const MacBotVariableForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<void>;
  deleteScenarioBlock: (block: any) => Promise<void>;
  changeScenarioBlock: (block: any) => Promise<void>;
  parentId: string | null;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);
  const components = {
    DropdownIndicator: null,
  };

  interface IUserInputForm extends INoteForm {
    VariableName: string;
    ValidatorType: string;
    ValidatorErrorMessage: string;
    VariableTitle: string;
  }

  const schema = Yup.object({
    VariableName: Yup.string()
      .max(31)
      .required(t('MacBotCreateVariableForm/key2') + ' ' + t('MacBotMediaForm/scenario_variable_name_required')),
    VariableTitle: Yup.string().required(t('MacBotCreateVariableForm/key2') + ' ' + t('MacBotMediaForm/scenario_variable_name_required')),
    ValidatorType: Yup.string().required(t(AlertsType.ISKBKO025)),
    ValidatorErrorMessage: Yup.string().required(t('MacBotCreateVariableForm/key9') + ' ' + t('MacBotMediaForm/scenario_variable_name_required')),
    ...getNoteSchema(t),
  });

  const [isDelete, setIsDelete] = useState(true);
  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    }
  }, [itemData]);

  const {
    register,
    formState: { errors },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control,
  } = useForm<IUserInputForm>({
    defaultValues: {
      VariableName: '',
      ValidatorType: 'Text',
      ValidatorErrorMessage: '',
      VariableTitle: '',
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
    resetOptions: {
      keepDefaultValues: true,
    },
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });

  useEffect(() => {
    reset();
  }, []);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData).then(res => {
      setShow(false);
    });
  };

  useEffect(() => {
    if (itemData && itemData?.validator) setValue('ValidatorType', itemData?.validator);
    if (itemData && itemData?.variableName) setValue('VariableName', itemData?.variableName);
    if (itemData && itemData?.exceptionMessage) setValue('ValidatorErrorMessage', itemData?.exceptionMessage);
    if (itemData && itemData?.name) setValue('VariableTitle', itemData?.name);
    if (itemData) {
      setValue('notes', itemData?.notes);
    }
  }, [itemData]);

  const ButtonSubmit = (data: IUserInputForm) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.Variable,
        parent: itemData.parent,
        validator: data.ValidatorType,
        variableName: data.VariableName,
        name: data.VariableTitle,
        title: data.VariableTitle,
        exceptionMessage: data.ValidatorErrorMessage,
        notes: data.notes,
      };
      changeScenarioBlock(_data).then(() => {
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.Variable,
        parent: parentId,
        validator: data.ValidatorType,
        variableName: data.VariableName,
        name: data.VariableTitle,
        title: data.VariableTitle,
        exceptionMessage: data.ValidatorErrorMessage,
        notes: data.notes,
      };
      createScenarioBlock(_data).then(() => {
        setShow(false);
      });
    }
  };

  useEffect(() => {
    let text = watch('VariableTitle');
    text = text.toLocaleLowerCase().replaceAll(' ', '_');

    setValue('VariableName', '$:var_' + text + '!');
  }, [watch('VariableTitle')]);

  useEffect(() => {
    if (!watch('ValidatorErrorMessage') || watch('ValidatorErrorMessage') == '') {
      setValue('ValidatorErrorMessage', t('MacBotVariableForm/e-04'));
    }
  }, []);

  return (
    <>
      <ScenarioSide onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowVariableForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="tw-self-stretch  tw-p-6 tw-bg-gray-50 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
          <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start  tw-flex">
            <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-4 tw-flex tw-flex-col  ">
              <div className="tw-w-full tw-grow tw-shrink tw-basis-0  tw-justify-start tw-items-start tw-gap-4 tw-flex">
                <Form.Group className="tw-w-full">
                  <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                    {t('MacBotCreateVariableForm/key2')}
                  </Form.Label>
                  <Form.Control
                    isInvalid={!!errors.VariableTitle}
                    className="tw-self-stretch  tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex"
                    placeholder="Enter a name for this variable"
                    max={31}
                    maxLength={31}
                    {...register('VariableTitle')}
                  />
                  <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                    {errors.VariableTitle?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="tw-w-full tw-grow tw-shrink tw-basis-0  tw-justify-start tw-items-center tw-gap-4 tw-flex">
                <Form.Group className="tw-w-full">
                  <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                    {t('MacBotCreateVariableForm/key9')}
                  </Form.Label>
                  <Form.Select
                    translate="no"
                    value={watch('ValidatorType')}
                    className="tw-self-stretch  tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex"
                    {...(register('ValidatorType'),
                    {
                      onChange: e => {
                        if (e?.target?.value) {
                          setValue('ValidatorType', e?.target?.value);
                          switch (e.target.value) {
                            case 'Text':
                              setValue('ValidatorErrorMessage', t('MacBotVariableForm/e-04'));
                              break;
                            case 'Number':
                              setValue('ValidatorErrorMessage', t('MacBotVariableForm/e-02'));
                              break;
                            case 'Email':
                              setValue('ValidatorErrorMessage', t('MacBotVariableForm/e-03'));
                              break;
                            default:
                              break;
                          }
                        }
                      },
                    })}
                  >
                    <option value="Text">Text</option>
                    <option value="Number">Number</option>
                    <option value="Email">Email</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </div>

          <div className="tw-mt-3 tw-self-stretch tw-justify-start  tw-items-start tw-gap-2 tw-flex tw-flex-col ">
            <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
              <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('MacBotCreateVariableForm/key11')}</div>
            </div>

            <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
              <CopyVariable text={watch('VariableTitle')} />
            </div>
          </div>
          <div className="tw-self-stretch tw-h-[0px] tw-border tw-border-gray-200"></div>
          <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
            <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
              <div className="tw-text-center tw-text-gray-500 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {t('MacBotVariableForm/e-title')}
              </div>
            </div>
            <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
              <div className="tw-grow tw-shrink tw-basis-0  tw-justify-start tw-items-start tw-gap-4 tw-flex">
                <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                  <Form.Group className="tw-w-full">
                    <Form.Control
                      className="tw-self-stretch tw-py-2 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex"
                      {...register('ValidatorErrorMessage')}
                      placeholder="Enter a message if user doesn’t respect the validator."
                    />
                    <div className="tw-flex tw-flex-col tw-justify-between tw-h-[50px]">
                      <Form.Control.Feedback type="invalid" className="d-block font-poppins tw-block tw-relative">
                        {errors.ValidatorErrorMessage?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center">
            {<VideoScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-variable-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </>
  );
};
